import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DialogService } from 'primeng/dynamicdialog';
import { TableSchemaEditLabelComponent } from './table-schema-edit-label/table-schema-edit-label.component';
import { TranslateService } from '@ngx-translate/core';
import { ConfiguratorDatastoreService, ConfiguratorStructureService, FieldTableDto, TableDto } from '@tj/tj-dmp-lib-api';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DatabaseUtilService } from '../../../services/database-util.service';
import { ConfirmationService, MenuItem, MessageService, SelectItem, SelectItemGroup } from 'primeng/api';
import { HttpErrorResponse } from '@angular/common/http';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { RoleModel } from '../../../auth/role.model';
import { AccountService } from '../../../auth/account.service';

import { isAdminOfTenant } from '../../../auth/admin-levels';
interface Field {
    name: string;
    code: string;
    label: string;
    labels: { [key: string]: string };
    description: string;
    type: string;
    categoryField: string;
    pk: boolean;
    notNull: boolean;
    autoIncrement: boolean;
    unique: boolean;
    editing: boolean;
    formattingType?: string;

    originalDescriptionValue: string; // stocke l'ancienne valeur de description dans le cas où l'utulisateur annulerait la modification
}

interface Table {
    id: string;
    code: string;
    name: string;
    label: string;
    labels: { [key: string]: string };
    schema: string;
    description: string;
    fields: Field[];
    versioning: boolean;
}

@Component({
    selector: 'dmp-table-schema',
    templateUrl: './table-schema.component.html',
    styleUrls: ['./table-schema.component.scss'],
    providers: [DialogService],
    standalone: false
})
export class TableSchemaComponent implements OnInit {

    public edit = false;

    role: RoleModel = RoleModel.newComplete();
    breadcrumb: MenuItem[] = [
        { label: this.translate.instant('menu.data-center.label') },
        { label: this.translate.instant('menu.data-center.tables'), routerLink: '/h/tables' },
        { label: this.translate.instant('tagsEvent.detailsMenuLabel') },
    ];
    codeTable: string;
    group: string;
    table: TableDto;
    editableDescription = false;
    fields: FieldTableDto[];
    sort = false;
    dialogAddField = false;

    formAddField: UntypedFormGroup;
    categories: Array<SelectItemGroup>;
    category: SelectItemGroup;
    types: Array<SelectItem>;
    partnersList: any[];
    /** Seul un Tenant Admin (ou Super Admin) peut changer l'affilié propriétaire — cf. back-end TableService. */
    isTenantAdmin = false;
    private tableOrigin: TableDto;


    constructor(
        private readonly structureResourceService: ConfiguratorStructureService,
        private readonly route: ActivatedRoute,
        private readonly dialogService: DialogService,
        private readonly translate: TranslateService,
        private readonly formBuilder: UntypedFormBuilder,
        private readonly databaseUtilService: DatabaseUtilService,
        protected confirmationService: ConfirmationService,
        private messageService: MessageService,
        private configuratorDatastoreService: ConfiguratorDatastoreService,
        private readonly accountService: AccountService
    ) {
    }

    ngOnInit(): void {
        this.codeTable = this.route.snapshot.paramMap.get('database');
        this.group = this.route.snapshot.paramMap.get('group');
        this.updateListField();
        this.categories = this.databaseUtilService.getListCategoriesTypes();
        this.types = this.categories[0].items;
        this.configuratorDatastoreService.getDatastoreUsingGET(this.group, true)
            .subscribe(datastore => this.partnersList = datastore.partners);
        this.accountService.fetch()
            .subscribe(account => this.isTenantAdmin = isAdminOfTenant(account, this.group));
    }

    onEditLabelTable() {
        const ref = this.dialogService.open(TableSchemaEditLabelComponent, {
            header: 'Edition du label de la table',
            data: {
                labels: this.table.labels,
                save: (data) => {
                    if (data) {
                        const labels = data as { [i18nCode: string]: string };
                        this.table.labels = labels;
                        this.table.label = labels.FR;
                        this.structureResourceService
                            .updateTableLabelUsingPUT(
                                this.group,
                                labels,
                                this.codeTable,
                                undefined,
                                this.table?.ownerPartner
                            )
                            .subscribe(() => {
                                // Not yet
                            });
                        ref.close();
                    }
                },
                cancel: () => {
                    ref.close();
                },
            },
            closable: true,
            width: '40em',
            contentStyle: { 'max-height': '600px', overflow: 'auto' },
        });
    }

    onEditLabelField(field: Field) {
        const ref = this.dialogService.open(TableSchemaEditLabelComponent, {
            header: 'Edition du label du champs',
            data: {
                labels: field.labels,
                save: (data) => {
                    if (data) {
                        const labels = data as { [i18nCode: string]: string };
                        field.labels = labels;
                        field.label = labels.FR;
                        this.structureResourceService
                            .updateFieldLabelUsingPUT(this.group, field.code, labels, this.codeTable, undefined, this.table?.ownerPartner)
                            .subscribe(res => {
                                // Not yet
                            });
                        ref.close();
                    }
                },
                cancel: () => {
                    ref.close();
                },
            },
            closable: true,
            width: '40em',
            contentStyle: { 'max-height': '600px', overflow: 'auto' },
        });
    }

    doEditDescription(description: string) {
        this.structureResourceService.updateTableDescriptionUsingPUT(this.group, description, this.codeTable, undefined, this.table?.ownerPartner)
            .subscribe(() => {
                this.editableDescription = false;
            });
    }

    updateFieldDescription(field: Field): void {
        this.structureResourceService.updateFieldDescription(this.group, field.code, field.description, undefined, this.codeTable, this.table?.ownerPartner)
            .subscribe(() => {
                field.editing = false;
            });
    }

    /** Propriétaire tel qu'enregistré avant l'édition en cours, pour avertir de la perte de droits. */
    get tableOriginOwnerPartner(): string {
        return this.tableOrigin?.ownerPartner ?? null;
    }

    /**
     * Libellé de l'affilié propriétaire ('Nom [CODE]'), pour l'affichage en consultation.
     */
    getOwnerPartnerLabel(): string {
        const code = this.table?.ownerPartner;
        if (!code) {
            return this.translate.instant('tableSchema.noOwnerLabel');
        }
        const partner = this.partnersList?.find(p => p.code === code);
        return partner ? `${partner.name} [${partner.code}]` : code;
    }

    /**
     * Noms des affiliés autorisés sur la table, pour l'affichage en consultation.
     */
    getSelectedPartnerNames(): string[] {
        const selectedIds = this.table?.partnersIdAcess;
        if (!selectedIds?.length || !this.partnersList?.length) {
            return [];
        }
        return this.partnersList
            .filter(partner => selectedIds.includes(partner.id))
            .map(partner => partner.name);
    }

    resetSort() {
        this.table.fields = this.fields.slice(0);
        this.sort = false;
    }

    handleSort() {
        this.sort = true;
    }


    createField() {
        this.formAddField = this.formBuilder.group({
            table: [this.codeTable, []],
            schema: [this.group, []],
            code: ['', [Validators.pattern('[-_a-z0-9]*'), Validators.required, Validators.minLength(2), Validators.maxLength(25)]],
            description: ['', [Validators.maxLength(100)]],
            labels: this.formBuilder.group({
                FR: ['', [Validators.required]],
                EN: ['', []],
                IT: ['', []]
            }),
            virtualField: [false, []],
            virtualFieldCalculation: ['', []],
            notNull: [false, []],
            categoryField: [this.categories[0].value, []],
            type: [this.categories[0].items[0].value, []],
            formattingType: ['', []],
            anonymizable: [false, []],
            autoIncrement: [false, []],
            displayableUIPersonal: [true, []],
            unique: [false, []],
            alertWhenChange: [false, []]
        });
        this.dialogAddField = true;
    }

    isCategoryText(): boolean {
        const categoryField = this.formAddField?.get('categoryField')?.value;
        return categoryField === 'TEXT';
    }

    doAddField() {
        if (!this.formAddField.invalid) {
            const dto: FieldTableDto = this.formAddField.value;

            this.structureResourceService
                .createFieldUsingPOST(this.group, dto, this.table?.ownerPartner)
                .pipe(catchError((error: HttpErrorResponse) => {
                    console.error('Error creating field:', error);
                    this.messageService.add({
                        severity: 'error',
                        summary: this.translate.instant('datastore.errorSummary'),
                        detail: error.error?.message || error.message || this.translate.instant('tableSchema.createFieldErrorDetail')
                    });
                    return throwError(error);
                }))
                .subscribe(res => {
                    this.messageService.add({
                        severity: 'success',
                        summary: this.translate.instant('datastore.successSummary'),
                        detail: this.translate.instant('tableSchema.fieldAddedDetail')
                    });
                    this.updateListField();
                    this.dialogAddField = false;
                });
        }
    }

    updateListType($event: any) {
        if ($event.value) {
            this.types = this.categories.find(c => c.value === $event.value).items;
            if ($event.value !== 'TEXT') {
                this.formAddField.get('formattingType').setValue('');
            }
        } else {
            this.types = [];
        }
    }

    getFormControlValueBoolean(formControlName: string): boolean {
        if (this.formAddField && this.formAddField.controls[formControlName]) {
            return (this.formAddField.controls[formControlName] as FormControl).value;
        } else {
            return false;
        }
    }

    delete(field: any, $event: MouseEvent) {
        this.confirmationService.confirm({
            target: $event.target,
            message: this.translate.instant('tableSchema.confirmDeleteFieldMessage', {code: field.code}),
            accept: () => {
                this.structureResourceService.deleteFieldUsingDELETE(this.group, field.code, this.codeTable, undefined, this.table?.ownerPartner).subscribe(() => {
                    this.updateListField();
                    this.messageService.add({
                        severity: 'success',
                        summary: this.translate.instant('datastore.successSummary'),
                        detail: this.translate.instant('tableSchema.fieldDeletedDetail')
                    });
                }, () => {
                    this.updateListField();
                    this.messageService.add({
                        severity: 'error',
                        summary: this.translate.instant('datastore.errorSummary'),
                        detail: this.translate.instant('landingPage.template.genericErrorDetail')
                    });
                });
            }
        });
    }

    private updateListField() {
        this.structureResourceService
            .getTableUsingGET(this.group, this.codeTable)
            .subscribe((result) => {
                this.table = result;
                this.fields = this.table.fields.slice(0);
            });
    }

    addVersioning() {
        this.structureResourceService.addVersioning(this.group, this.codeTable).subscribe(() => {
            this.updateListField();
            this.messageService.add({
                severity: 'success',
                summary: this.translate.instant('datastore.successSummary'),
                detail: this.translate.instant('tableSchema.versioningAddedDetail')
            });
        });
    }

    onAnonymizable(field: any, event: any) {
        this.table.id;
        this.table.schema;
        field.code;
        event.checked;
    }

    startEditDescription(field: Field): void {
        field.editing = true;
        field.originalDescriptionValue = field.description;
        this.focusDescriptionInput(field.code);
    }

    cancelEditDescription(field: Field): void {
        field.editing = false;
        field.description = field.originalDescriptionValue;
    }

    onEditInfo() {
        this.edit = true;
        this.tableOrigin = JSON.parse(JSON.stringify(this.table));
    }

    onResetInfo() {
        this.edit = false;
        this.table = JSON.parse(JSON.stringify(this.tableOrigin));
    }

    onSaveInfo() {
        this.structureResourceService.updateTableInfo(this.table as TableDto, this.group, this.table.id, this.tableOrigin?.ownerPartner)
            .subscribe(() => {
                this.edit = false;
                this.updateListField();
                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('datastore.successSummary'),
                    detail: this.translate.instant('tableSchema.infoSavedDetail')
                });
            }, () => {
                // On reste en édition pour ne pas perdre la saisie.
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('datastore.errorSummary'),
                    detail: this.translate.instant('tableSchema.infoSaveFailedDetail')
                });
            });
    }

    /**
     * Retourne le label du formatage en fonction de sa valeur
     */
    getFormattingLabel(formattingType: string): string {
        switch (formattingType) {
            case 'lowerCase':
                return this.translate.instant('fields.formatting.lowerCase');
            case 'upperCase':
                return this.translate.instant('fields.formatting.upperCase');
            case 'upperCaseFirst':
                return this.translate.instant('fields.formatting.upperCaseFirst');
            case '':
            case null:
            case undefined:
                return this.translate.instant('fields.formatting.none');
            default:
                return formattingType;
        }
    }

    private focusDescriptionInput(fieldCode: string): void {
        setTimeout(() => {
            const inputId = `field-description-input-${fieldCode}`;
            const input = document.getElementById(inputId) as HTMLInputElement | null;
            input?.focus();
            input?.select();
        });
    }
}
